import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Bell, BellOff, Play, Pencil, Trash2, Search, Clock } from 'lucide-react';

const LICENSE_TYPES = ['LBSW', 'LMSW', 'LCSW', 'LCSW-S', 'LPC', 'LMFT', 'MPH'];
const JOB_TYPES = ['full-time', 'part-time', 'prn', 'contract', 'temporary', 'internship'];
const FREQ_LABEL = { immediate: 'Immediate', daily: 'Daily', weekly: 'Weekly', off: 'Off' };

const chipsFor = (s) => {
  const c = [];
  if (s.search) c.push(`"${s.search}"`);
  (s.license_types || []).forEach((l) => c.push(l));
  (s.job_types || []).forEach((j) => c.push(j));
  if (s.location) c.push(s.location);
  if (s.zip_code) c.push(`${s.zip_code}${s.radius ? ` (${s.radius}mi)` : ''}`);
  if (s.remote_type) c.push(s.remote_type.replace('_', ' '));
  if (s.salary_min || s.salary_max) c.push(`$${s.salary_min || 0}–${s.salary_max || '∞'}`);
  if (s.specialty) c.push(s.specialty);
  if (s.lgbtq_affirming) c.push('LGBTQ+ affirming');
  if (s.veteran_friendly) c.push('Veteran-friendly');
  if (s.accessible_inclusive) c.push('Accessible');
  return c;
};

const JobSeekerTabAlerts = () => {
  const navigate = useNavigate();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API_URL}/saved-searches`); setSearches(res.data.saved_searches || []); }
    catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runAgain = (s) => {
    const p = new URLSearchParams();
    if (s.search) p.set('search', s.search);
    if (s.location) p.set('location', s.location);
    if (s.license_types?.[0]) p.set('license', s.license_types[0]);
    if (s.job_types?.[0]) p.set('job_type', s.job_types[0]);
    if (s.remote_type === 'remote') p.set('is_remote', 'true');
    navigate(`/jobs?${p.toString()}`);
  };

  const toggle = async (s) => {
    try { const res = await axios.put(`${API_URL}/saved-searches/${s.search_id}/toggle`); toast.success(res.data.message); await load(); }
    catch (e) { toast.error('Could not update.'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this saved search and its alerts?')) return;
    try { await axios.delete(`${API_URL}/saved-searches/${id}`); toast.success('Deleted.'); await load(); }
    catch (e) { toast.error('Could not delete.'); }
  };

  const openEdit = (s) => { setForm({ ...s }); setOpen(true); };
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleList = (k, val) => setForm((f) => {
    const arr = f[k] || [];
    return { ...f, [k]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
  });

  const saveEdit = async () => {
    if (!form.name?.trim()) { toast.error('Name is required.'); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/saved-searches/${form.search_id}`, {
        name: form.name, frequency: form.frequency,
        search: form.search || null, license_types: form.license_types || [],
        location: form.location || null, zip_code: form.zip_code || null,
        radius: form.radius ? Number(form.radius) : null, remote_type: form.remote_type || null,
        job_types: form.job_types || [], salary_min: form.salary_min ? Number(form.salary_min) : null,
        salary_max: form.salary_max ? Number(form.salary_max) : null, specialty: form.specialty || null,
        lgbtq_affirming: !!form.lgbtq_affirming, veteran_friendly: !!form.veteran_friendly,
        accessible_inclusive: !!form.accessible_inclusive,
      });
      toast.success('Search updated.');
      setOpen(false); await load();
    } catch (e) { toast.error('Could not save.'); } finally { setSaving(false); }
  };

  if (loading) return <div className="py-12 text-center text-gray-500">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="alerts-tab">
      <div className="bg-primary/5 border border-primary/15 rounded-xl p-4 flex items-start gap-3">
        <Bell className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-600">Save any job search from the <button className="text-secondary underline" onClick={() => navigate('/jobs')}>Jobs page</button> to get alerted about new matches. Your saved searches are private to your account.</p>
      </div>

      {searches.length === 0 ? (
        <div className="bg-white rounded-xl border text-center py-12" data-testid="no-alerts">
          <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-medium text-gray-900 mb-2">No saved searches yet</h3>
          <p className="text-gray-500 mb-4">Save a search to get job alerts by email.</p>
          <Button className="bg-primary text-white" onClick={() => navigate('/jobs')}>Browse Jobs</Button>
        </div>
      ) : (
        <div className="grid gap-3">
          {searches.map((s) => (
            <div key={s.search_id} className="bg-white rounded-xl border p-5" data-testid={`saved-search-${s.search_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h4 className="font-serif text-lg font-bold text-primary">{s.name}</h4>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {chipsFor(s).length ? chipsFor(s).map((c, i) => <Badge key={i} className="bg-gray-100 text-gray-600">{c}</Badge>) : <span className="text-xs text-gray-400">All jobs</span>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge className={s.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}>{s.status === 'active' ? `Alerts: ${FREQ_LABEL[s.frequency] || s.frequency}` : 'Paused'}</Badge>
                  {s.new_match_count > 0 && <span className="text-xs font-semibold text-secondary" data-testid={`new-matches-${s.search_id}`}>{s.new_match_count} new match{s.new_match_count === 1 ? '' : 'es'}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-400 mt-3">
                <Clock className="w-3.5 h-3.5" /> Last checked: {s.last_processed ? new Date(s.last_processed).toLocaleString() : 'Not yet'}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => runAgain(s)} data-testid={`run-search-${s.search_id}`}><Search className="w-4 h-4 mr-1" /> Run Again ({s.new_match_count})</Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`edit-search-${s.search_id}`}><Pencil className="w-4 h-4 mr-1" /> Edit</Button>
                <Button size="sm" variant="outline" onClick={() => toggle(s)} data-testid={`toggle-search-${s.search_id}`}>
                  {s.status === 'active' ? <><BellOff className="w-4 h-4 mr-1" /> Pause</> : <><Bell className="w-4 h-4 mr-1" /> Reactivate</>}
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(s.search_id)} data-testid={`delete-search-${s.search_id}`}><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {form && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit Saved Search</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs text-gray-500 mb-1 block">Name</Label><Input value={form.name || ''} onChange={(e) => setF('name', e.target.value)} data-testid="edit-search-name" /></div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Alert frequency</Label>
                  <Select value={form.frequency} onValueChange={(v) => setF('frequency', v)}>
                    <SelectTrigger data-testid="edit-search-frequency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediate">Immediately</SelectItem>
                      <SelectItem value="daily">Daily summary</SelectItem>
                      <SelectItem value="weekly">Weekly summary</SelectItem>
                      <SelectItem value="off">Off</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-2 block">License types</Label>
                <div className="flex flex-wrap gap-2">
                  {LICENSE_TYPES.map((l) => (
                    <button key={l} type="button" onClick={() => toggleList('license_types', l)} className={`px-3 py-1 rounded-full text-sm border ${(form.license_types || []).includes(l) ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200'}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-2 block">Employment type</Label>
                <div className="flex flex-wrap gap-2">
                  {JOB_TYPES.map((j) => (
                    <button key={j} type="button" onClick={() => toggleList('job_types', j)} className={`px-3 py-1 rounded-full text-sm border capitalize ${(form.job_types || []).includes(j) ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200'}`}>{j.replace('-', ' ')}</button>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs text-gray-500 mb-1 block">Keyword</Label><Input value={form.search || ''} onChange={(e) => setF('search', e.target.value)} /></div>
                <div><Label className="text-xs text-gray-500 mb-1 block">City / State</Label><Input value={form.location || ''} onChange={(e) => setF('location', e.target.value)} /></div>
                <div><Label className="text-xs text-gray-500 mb-1 block">ZIP code</Label><Input value={form.zip_code || ''} onChange={(e) => setF('zip_code', e.target.value)} /></div>
                <div><Label className="text-xs text-gray-500 mb-1 block">Radius (miles)</Label><Input type="number" value={form.radius || ''} onChange={(e) => setF('radius', e.target.value)} /></div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Work setting</Label>
                  <Select value={form.remote_type || 'any'} onValueChange={(v) => setF('remote_type', v === 'any' ? null : v)}>
                    <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="in_person">In-person</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                      <SelectItem value="remote">Remote</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs text-gray-500 mb-1 block">Specialty</Label><Input value={form.specialty || ''} onChange={(e) => setF('specialty', e.target.value)} /></div>
                <div><Label className="text-xs text-gray-500 mb-1 block">Min salary</Label><Input type="number" value={form.salary_min || ''} onChange={(e) => setF('salary_min', e.target.value)} /></div>
                <div><Label className="text-xs text-gray-500 mb-1 block">Max salary</Label><Input type="number" value={form.salary_max || ''} onChange={(e) => setF('salary_max', e.target.value)} /></div>
              </div>
              <div className="flex flex-wrap gap-5">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"><Checkbox checked={!!form.lgbtq_affirming} onCheckedChange={(v) => setF('lgbtq_affirming', !!v)} /> LGBTQ+ affirming</label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"><Checkbox checked={!!form.veteran_friendly} onCheckedChange={(v) => setF('veteran_friendly', !!v)} /> Veteran-friendly</label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"><Checkbox checked={!!form.accessible_inclusive} onCheckedChange={(v) => setF('accessible_inclusive', !!v)} /> Accessible/inclusive</label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-primary text-white" onClick={saveEdit} disabled={saving} data-testid="save-edit-search">{saving ? 'Saving…' : 'Save Changes'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default JobSeekerTabAlerts;
